// Une tâche récurrente tombe-t-elle tel jour ? (#407)
//
// La règle vivait en trois versions qui ne disaient pas la même chose : la
// journée de l'enfant (dailyCompletion.service), les séries et le plan de la
// semaine (lib/taskSchedule), et un getter du store mort depuis longtemps.
// Elles divergeaient sur `frequency = 'custom'` : la première lisait les jours,
// la deuxième les ignorait. Une tâche « une semaine sur deux » aurait été
// comptée prévue la semaine de repos par les séries, et les aurait cassées.
//
// Ce module ne regarde QUE le calendrier : la fréquence, les jours, l'intervalle
// de semaines et la semaine du mois. L'activité, l'assignation, la fenêtre
// start/end, l'objectif hebdo et les jours d'absence restent aux appelants.

import { addDays } from '@/services/date.service'
import { weekWindowFor } from '@/utils/weeklyTarget'

export const REPEAT_EVERY_WEEKS_MAX = 4
/** 1 à 4 : la Nième semaine du mois ; -1 : la dernière. */
export const MONTH_WEEKS = [1, 2, 3, 4, -1] as const
export type MonthWeek = (typeof MONTH_WEEKS)[number]

const DAY_MS = 24 * 60 * 60 * 1000

/** Toutes les N semaines (1 à 4). Absent, nul ou hors bornes = chaque semaine.
 *  Paramètre `unknown` : la colonne n'est pas dans les types DB générés, comme
 *  weekly_target_count. */
export function repeatEveryWeeksOf(task: unknown): number {
    const n = Number((task as { repeat_every_weeks?: number | null } | null | undefined)?.repeat_every_weeks ?? 1)
    return Number.isInteger(n) && n >= 1 && n <= REPEAT_EVERY_WEEKS_MAX ? n : 1
}

/** La semaine du mois d'une tâche mensuelle, null sinon. */
export function monthWeekOf(task: unknown): MonthWeek | null {
    const n = Number((task as { month_week?: number | null } | null | undefined)?.month_week ?? 0)
    return (MONTH_WEEKS as readonly number[]).includes(n) ? n as MonthWeek : null
}

/** La semaine de départ du cycle : `repeat_anchor_date`, sinon la création. */
export function repeatAnchorOf(task: unknown): string | null {
    const t = task as { repeat_anchor_date?: string | null; start_date?: string | null } | null | undefined
    const raw = t?.repeat_anchor_date || t?.start_date
    return raw ? String(raw).slice(0, 10) : null
}

/** 'YYYY-MM-DD' → jour de la semaine (0 = dimanche), lu à midi UTC pour que le
 *  fuseau de l'appareil ne déplace pas le jour calendaire. */
export function weekdayOf(date: string): number {
    return new Date(`${date}T12:00:00Z`).getUTCDay()
}

/** Écart en semaines entre la semaine de `from` et celle de `to`. */
export function weeksBetween(from: string, to: string, weekStartIndex: number): number {
    const a = Date.parse(`${weekWindowFor(from, weekStartIndex).start}T12:00:00Z`)
    const b = Date.parse(`${weekWindowFor(to, weekStartIndex).start}T12:00:00Z`)
    return Math.round((b - a) / (7 * DAY_MS))
}

/** Le jour tombe-t-il dans la Nième (ou la dernière) semaine de son mois ? La
 *  « première semaine » compte les jours 1 à 7 : le premier vendredi du mois est
 *  le vendredi qui tombe entre le 1er et le 7. */
export function isInMonthWeek(date: string, monthWeek: MonthWeek): boolean {
    const day = Number(date.slice(8, 10))
    if (monthWeek === -1) {
        // Le dernier : sept jours plus tard, on a changé de mois.
        return addDays(date, 7).slice(5, 7) !== date.slice(5, 7)
    }
    return Math.ceil(day / 7) === monthWeek
}

export interface RecurrenceFields {
    frequency?: string | null
    days_of_week?: number[] | null
    start_date?: string | null
}

/**
 * La tâche tombe-t-elle ce jour-là, d'après son seul calendrier ?
 *
 * - Mensuelle (`month_week`) : le jour choisi, dans la bonne semaine du mois.
 * - Sinon, une fréquence autre que `daily` filtre par `days_of_week` quand la
 *   liste en contient (vide = chaque jour, comme la journée de l'enfant l'a
 *   toujours lu, `custom` compris) ; puis l'intervalle de semaines, compté
 *   depuis la semaine de l'ancre.
 */
export function isRecurringOn(task: RecurrenceFields, date: string, weekStartIndex: number): boolean {
    const days = task.days_of_week ?? []
    const weekday = weekdayOf(date)

    const monthWeek = monthWeekOf(task)
    if (monthWeek !== null) {
        return days.includes(weekday) && isInMonthWeek(date, monthWeek)
    }

    if (task.frequency !== 'daily' && days.length > 0 && !days.includes(weekday)) return false

    const every = repeatEveryWeeksOf(task)
    if (every === 1) return true
    const anchor = repeatAnchorOf(task)
    if (!anchor) return true
    try {
        const steps = weeksBetween(anchor, date, weekStartIndex)
        return ((steps % every) + every) % every === 0
    } catch {
        // Une ancre corrompue ne doit pas éteindre la tâche.
        return true
    }
}

/** Les prochains jours où la tâche tombe, à partir de `from` inclus. Sert
 *  d'aperçu dans le formulaire. Bornée à un an pour ne jamais boucler. */
export function nextOccurrences(
    task: RecurrenceFields,
    from: string,
    count: number,
    weekStartIndex: number,
): string[] {
    const out: string[] = []
    for (let i = 0; i < 366 && out.length < count; i++) {
        const date = addDays(from, i)
        if (isRecurringOn(task, date, weekStartIndex)) out.push(date)
    }
    return out
}
